import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  fetchElectiveGroups,
  createElectiveGroup,
  updateElectiveGroup,
  deleteElectiveGroup
} from './electiveGroupApi';
import { useAcademicData } from './useAcademicData';
import api from '../../api';

// Professional UI Components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, variant = 'primary', size = 'md', className = '', ...props }) => {
  const baseClasses = 'font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center gap-2';
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 shadow-xs',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 focus:ring-gray-500 border border-gray-200',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 shadow-xs',
    success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 shadow-xs',
    ghost: 'bg-transparent hover:bg-gray-50 text-gray-600 focus:ring-gray-400',
    language: 'bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500 shadow-xs',
    elective: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 shadow-xs'
  };
  const sizes = {
    sm: 'px-3 py-2 sm:py-1.5 text-xs sm:text-sm',
    md: 'px-4 py-3 sm:py-2 text-sm',
    lg: 'px-6 py-4 sm:py-3 text-base'
  };
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ label, error, className = '', ...props }) => (
  <div className={className}>
    {label && <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1.5">{label}</label>}
    <input
      className={`w-full px-3 py-3 sm:py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none outline-none ${
        error ? 'border-red-500 bg-red-50/30' : 'border-gray-300 bg-white'
      }`}
      {...props}
    />
    {error && <p className="mt-1.5 text-xs text-red-600 font-medium">{error}</p>}
  </div>
);

const Select = ({ label, error, className = '', children, ...props }) => (
  <div className={className}>
    {label && <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1.5">{label}</label>}
    <select
      className={`w-full px-3 py-3 sm:py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white outline-none ${
        error ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
      }`}
      {...props}
    >
      {children}
    </select>
    {error && <p className="mt-1.5 text-xs text-red-600 font-medium">{error}</p>}
  </div>
);

const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800 border border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    danger: 'bg-red-100 text-red-800 border border-red-200',
    info: 'bg-blue-100 text-blue-800 border border-blue-200',
    language: 'bg-purple-100 text-purple-800 border border-purple-200',
    elective: 'bg-blue-100 text-blue-800 border border-blue-200'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-bold ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

const LoadingSpinner = () => (
  <div className="flex flex-col justify-center items-center py-12 gap-2">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span className="text-xs text-gray-400 font-medium">Loading roster parameters...</span>
  </div>
);

const EmptyState = ({ title, description, action }) => (
  <div className="text-center py-12 px-4">
    <div className="mx-auto h-16 w-16 text-gray-300 mb-4">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    </div>
    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5">{title}</h3>
    <p className="text-xs sm:text-sm text-gray-500 mb-5 max-w-sm mx-auto">{description}</p>
    <div className="w-full sm:w-auto inline-block">{action}</div>
  </div>
);

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-2xl',
    lg: 'sm:max-w-4xl',
    xl: 'sm:max-w-5xl'
  };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
      <div className="fixed inset-0" onClick={onClose}></div>
      <div className={`relative bg-white w-full h-[100dvh] sm:h-auto ${sizes[size]} sm:rounded-2xl text-left shadow-2xl transform transition-all flex flex-col overflow-hidden`}>
        <div className="bg-white px-4 py-4 sm:px-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate pr-4">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-50 transition flex-shrink-0"
              aria-label="Close form view"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
};

const ModalFooter = ({ children }) => (
  <div className="bg-gray-50 px-4 py-4 sm:px-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row justify-end gap-2 flex-shrink-0">
    {children}
  </div>
);

const MessageBanner = React.forwardRef(({ type = 'info', children, onClose }, ref) => {
  const bg = type === 'success' ? 'bg-green-50 border-green-200 text-green-800'
    : type === 'error' ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-800';
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      className={`p-4 mb-4 border rounded-xl text-sm ${bg} flex items-start justify-between gap-3`}
    >
      <div className="flex-1 break-words">{children}</div>
      {onClose && (
        <button onClick={onClose} className="text-xs font-bold underline opacity-80 hover:opacity-100 whitespace-nowrap p-0.5">Dismiss</button>
      )}
    </div>
  );
});

// Main Component
const ElectiveGroupManager = ({ tenantId, departmentId, addToast, token, department }) => {
  const {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    isSemesterBased,
    semesters,
    selectedSemester,
    setSelectedSemester
  } = useAcademicData(tenantId || department?.tenantId, token);

  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [viewGroup, setViewGroup] = useState(null);
  const [facultyBySubject, setFacultyBySubject] = useState({});
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    groupName: '',
    label: '',
    academicYearId: '',
    semesterId: '',
    termId: '',
    groupCategory: 'elective',
    subjects: []
  });
  const [editingGroup, setEditingGroup] = useState(null);
  const [formElectiveSubjects, setFormElectiveSubjects] = useState([]);
  const [electiveSubjects, setElectiveSubjects] = useState([]);
  const [languageSubjects, setLanguageSubjects] = useState([]);
  const [formLanguageSubjects, setFormLanguageSubjects] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showWhySubgroup, setShowWhySubgroup] = useState({});
  const [activeTab, setActiveTab] = useState('elective');

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const messageRef = useRef(null);
  
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchTerms = useCallback(async (academicYearId) => {
    if (!academicYearId || !tenantId) {
      setTerms([]);
      return;
    }
    try {
      const res = await api.get(`/api/academic-year/${academicYearId}/terms?tenantId=${tenantId}`);
      const fetchedTerms = res.data.terms || [];
      setTerms(fetchedTerms);
    } catch (err) {
      console.warn('Error fetching terms:', err);
      setTerms([]);
    }
  }, [tenantId]);

  useEffect(() => {
    if (selectedAcademicYear) {
      const year = academicYears.find(y => y._id === selectedAcademicYear);
      if (year && !year.isSemesterBased) {
        fetchTerms(selectedAcademicYear);
        setSelectedTerm('');
      } else {
        setTerms([]);
        setSelectedTerm('');
      }
    }
  }, [selectedAcademicYear, academicYears, fetchTerms]);

  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const params = {
          tenantId: tenantId || department?.tenantId,
          departmentId,
          academicYearId: selectedAcademicYear,
          groupCategory: activeTab
        };
        const academicYear = academicYears.find(y => y._id === selectedAcademicYear);
        if (academicYear?.isSemesterBased) {
          if (selectedSemester) {
            params.semesterId = selectedSemester;
          }
        } else {
          if (selectedTerm) {
            params.termId = selectedTerm;
          }
        }
        const data = await fetchElectiveGroups(params, token);
        setGroups(data);
      } catch (err) {
        setGroups([]);
        setMessage('Failed to fetch groups');
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    };
    const academicYear = academicYears.find(y => y._id === selectedAcademicYear);
    if ((tenantId || department?.tenantId) && departmentId && selectedAcademicYear) {
      if (academicYear && !academicYear.isSemesterBased && !selectedTerm) {
        setGroups([]);
        return;
      }
      fetchGroups();
    }
  }, [tenantId, department?.tenantId, departmentId, selectedAcademicYear, selectedSemester, selectedTerm, token, activeTab, academicYears]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setElectiveSubjects([]);
        setLanguageSubjects([]);
        const tenant = tenantId || department?.tenantId;
        if (!departmentId || !tenant || !selectedAcademicYear) return;

        const params = new URLSearchParams({
          tenantId: tenant,
          academicYearId: selectedAcademicYear,
          departmentId
        });

        const academicYear = academicYears.find(y => y._id === selectedAcademicYear);
        if (academicYear?.isSemesterBased && selectedSemester) {
          params.append('semesterId', selectedSemester);
        } else if (!academicYear?.isSemesterBased && selectedTerm) {
          params.append('termId', selectedTerm);
        }

        const url = `/api/subjects/class-manager/subjects/department/context?${params.toString()}`;
        const res = await api.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const all = res.data?.data?.subjects?.all || [];
        const allSubjects = all.map(subject => ({
          ...subject,
          id: subject._id || subject.id,
          code: subject.subjectCode || subject.code
        }));

        let filteredSubjects = allSubjects;
        if (!academicYear?.isSemesterBased && selectedTerm) {
          filteredSubjects = allSubjects.filter(s => {
            if (s.termId) {
              return String(s.termId) === String(selectedTerm);
            }
            return false;
          });
        }

        const electiveSubs = filteredSubjects.filter(
          s => s.isElective === true && s.isLanguage !== true
        );
        const languageSubs = filteredSubjects.filter(
          s => s.isLanguage === true
        );

        setElectiveSubjects(electiveSubs);
        setLanguageSubjects(languageSubs);
      } catch (err) {
        console.error('Error fetching subjects for department:', err);
        setElectiveSubjects([]);
        setLanguageSubjects([]);
        setMessage('Error fetching subjects for department');
        setMessageType('error');
      }
    };
    fetchSubjects();
  }, [departmentId, tenantId, department?.tenantId, token, selectedAcademicYear, isSemesterBased, selectedSemester, selectedTerm, academicYears]);

  useEffect(() => {
    const fetchFormSubjects = async () => {
      try {
        setFormElectiveSubjects([]);
        setFormLanguageSubjects([]);
        const tenant = tenantId || department?.tenantId;
        if (!departmentId || !tenant || !form.academicYearId) return;

        const params = new URLSearchParams({
          tenantId: tenant,
          academicYearId: form.academicYearId,
          departmentId
        });

        const academicYear = academicYears.find(y => y._id === form.academicYearId);
        const isSemesterBasedYear = academicYear?.isSemesterBased || false;

        if (isSemesterBasedYear) {
          if (form.semesterId) params.append('semesterId', form.semesterId);
        } else {
          if (form.termId) params.append('termId', form.termId);
        }

        const url = `/api/subjects/class-manager/subjects/department/context?${params.toString()}`;
        const res = await api.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const all = res.data?.data?.subjects?.all || [];
        const allSubjects = all.map(subject => ({
          ...subject,
          id: subject._id || subject.id,
          code: subject.subjectCode || subject.code
        }));

        let filteredSubjects = allSubjects;
        if (!academicYear?.isSemesterBased && form.termId) {
          filteredSubjects = allSubjects.filter(s => {
            if (s.termId) {
              return String(s.termId) === String(form.termId);
            }
            return false;
          });
        }

        const electiveSubs = filteredSubjects.filter(
          s => s.isElective === true && s.isLanguage !== true
        );
        const languageSubs = filteredSubjects.filter(
          s => s.isLanguage === true
        );

        setFormElectiveSubjects(electiveSubs);
        setFormLanguageSubjects(languageSubs);
      } catch (err) {
        console.error('Error fetching FORM subjects:', err);
        setFormElectiveSubjects([]);
        setFormLanguageSubjects([]);
      }
    };
    if (showModal) {
      fetchFormSubjects();
    }
  }, [showModal, departmentId, tenantId, department?.tenantId, token, form.academicYearId, form.semesterId, form.termId, academicYears]);

  const mapFacultyList = (facultyArr) =>
    (facultyArr || []).map(f => ({
      ...f,
      id: f._id ? f._id.toString() : (f.id ? f.id.toString() : ''),
      staffCode: f.staffCode || ''
    }));

  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteElectiveGroup(groupId, tenantId || department?.tenantId, token);
      setMessage('Group deleted successfully');
      setMessageType('success');
      setGroups(groups.filter(g => g._id !== groupId));
    } catch (err) {
      setMessage('Failed to delete group');
      setMessageType('error');
    }
  };

  const openModal = async (group = null) => {
    setFormErrors({});
    if (group) {
      setEditingGroup(group);
      const subjectsForForm = group.subjects.map(s => ({
        subjectId: (s.subjectId && typeof s.subjectId === 'object' && s.subjectId.$oid) ? s.subjectId.$oid : (s.subjectId || ''),
        facultyId: (s.facultyId && typeof s.facultyId === 'object' && s.facultyId.$oid) ? s.facultyId.$oid : (s.facultyId || ''),
        subGroups: (s.subGroups || []).map(sg => ({
          ...sg,
          facultyId: (sg.facultyId && typeof sg.facultyId === 'object' && sg.facultyId.$oid) ? sg.facultyId.$oid : (sg.facultyId || ''),
          subGroupId: sg.subGroupId || ''
        }))
      }));

      setForm({
        groupName: group.groupName,
        label: group.label || '',
        academicYearId: group.academicYearId,
        semesterId: group.semesterId || '',
        termId: group.termId || '',
        groupCategory: group.groupCategory || 'elective',
        subjects: subjectsForForm
      });

      const academicYear = academicYears.find(y => y._id === group.academicYearId);
      if (academicYear && !academicYear.isSemesterBased) {
        fetchTerms(group.academicYearId);
      }

      for (const s of group.subjects) {
        try {
          const subjectId = typeof s.subjectId === 'object' && s.subjectId.$oid ? s.subjectId.$oid : s.subjectId;
          const response = await api.get(
            `/api/subjects/get-faculty-by-subject/${subjectId}/${tenantId || department?.tenantId}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );
          setFacultyBySubject(prev => ({
            ...prev,
            [subjectId]: mapFacultyList(response.data.faculty)
          }));
        } catch (err) {
          setFacultyBySubject(prev => ({ ...prev, [s.subjectId]: [] }));
        }
      }
    } else {
      setEditingGroup(null);
      setForm({
        groupName: '',
        label: '',
        academicYearId: selectedAcademicYear,
        semesterId: selectedSemester || '',
        termId: selectedTerm || '',
        groupCategory: activeTab,
        subjects: []
      });
      
      const academicYear = academicYears.find(y => y._id === selectedAcademicYear);
      if (academicYear && !academicYear.isSemesterBased) {
        fetchTerms(selectedAcademicYear);
      }
    }
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.groupName.trim()) errors.groupName = 'Group name is required';
    if (!form.label.trim()) errors.label = 'Label is required';
    if (!form.academicYearId) errors.academicYearId = 'Academic year is required';
    
    const academicYear = academicYears.find(y => y._id === form.academicYearId);
    const isSemesterBasedYear = academicYear?.isSemesterBased || false;
    
    if (isSemesterBasedYear) {
      if (!form.semesterId) errors.semesterId = 'Semester is required';
    } else {
      if (!form.termId) errors.termId = 'Term is required';
    }
    
    if (form.subjects.length === 0) errors.subjects = `At least one ${form.groupCategory === 'language' ? 'language' : 'subject'} must be selected`;
    for (const s of form.subjects) {
      if (!s.subGroups || s.subGroups.length === 0) {
        if (!s.facultyId) errors[`subject-${s.subjectId}`] = 'Faculty assignment is required';
      } else {
        for (const [idx, sg] of s.subGroups.entries()) {
          if (!sg.facultyId) errors[`subgroup-${s.subjectId}-${idx}`] = 'Faculty assignment is required for subgroup';
        }
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMessage('Please fix the form errors before submitting');
      setMessageType('error');
      return;
    }
    
    const cleanedSubjects = form.subjects.map(s => ({
      subjectId: s.subjectId,
      facultyId: s.facultyId || undefined,
      groupType: form.groupCategory,
      subGroups: (s.subGroups || []).map(sg => ({
        subGroupId: sg.subGroupId,
        facultyId: sg.facultyId || undefined
      }))
    }));
    
    const payload = {
      tenantId: tenantId || department?.tenantId,
      departmentId,
      academicYearId: form.academicYearId,
      groupName: form.groupName,
      label: form.label,
      groupCategory: form.groupCategory,
      status: "Active",
      subjects: cleanedSubjects,
    };
    
    const academicYear = academicYears.find(y => y._id === form.academicYearId);
    const isSemesterBasedYear = academicYear?.isSemesterBased || false;
    
    if (isSemesterBasedYear) {
      if (form.semesterId) payload.semesterId = form.semesterId;
      payload.termId = null;
    } else {
      if (form.termId) payload.termId = form.termId;
      payload.semesterId = null;
    }
    
    try {
      let result;
      if (editingGroup) {
        result = await updateElectiveGroup(editingGroup._id, payload, token);
        setMessage(`${form.groupCategory === 'language' ? 'Language' : 'Elective'} group updated successfully`);
        setMessageType('success');
        setGroups(groups.map(g => (g._id === editingGroup._id ? result.data : g)));
        setShowModal(false);
      } else {
        result = await createElectiveGroup(payload, token);
        setMessage(`${form.groupCategory === 'language' ? 'Language' : 'Elective'} group created successfully`);
        setMessageType('success');
        setGroups([...groups, result.data]);
        setShowModal(false);
        setShowSuccessModal(true);
      }
    } catch (err) {
      setMessage('Failed to save group');
      setMessageType('error');
    }
  };

  const handleSubjectCheckbox = async (subj, checked) => {
    setForm(f => {
      if (checked) {
        return {
          ...f,
          subjects: [
            ...f.subjects,
            { subjectId: subj.id, facultyId: '', subGroups: [], groupType: f.groupCategory }
          ]
        };
      } else {
        return {
          ...f,
          subjects: f.subjects.filter(s => s.subjectId !== subj.id)
        };
      }
    });
    if (checked) {
      try {
        const response = await api.get(
          `/api/subjects/get-faculty-by-subject/${subj.id}/${tenantId || department?.tenantId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setFacultyBySubject(prev => ({
          ...prev,
          [subj.id]: mapFacultyList(response.data.faculty)
        }));
      } catch (err) {
        setMessage('Error fetching faculty');
        setMessageType('error');
        setFacultyBySubject(prev => ({ ...prev, [subj.id]: [] }));
      }
    } else {
      setFacultyBySubject(prev => {
        const updated = { ...prev };
        delete updated[subj.id];
        return updated;
      });
    }
  };

  const handleAddSubGroup = (subjectId) => {
    setForm(f => ({
      ...f,
      subjects: f.subjects.map(s => {
        if (s.subjectId === subjectId) {
          const hadNoSubgroups = !s.subGroups || s.subGroups.length === 0;
          return {
            ...s,
            facultyId: hadNoSubgroups ? '' : s.facultyId,
            subGroups: [
              ...(s.subGroups || []),
              { facultyId: '' }
            ]
          };
        }
        return s;
      })
    }));
  };

  const handleSubGroupChange = (subjectId, idx, field, value) => {
    setForm(f => ({
      ...f,
      subjects: f.subjects.map(s =>
        s.subjectId === subjectId
          ? {
              ...s,
              subGroups: s.subGroups.map((sg, i) =>
                i === idx ? { ...sg, [field]: value } : sg
              )
            }
          : s
      )
    }));
  };

  const handleRemoveSubGroup = (subjectId, idx) => {
    setForm(f => ({
      ...f,
      subjects: f.subjects.map(s =>
        s.subjectId === subjectId
          ? {
              ...s,
              subGroups: s.subGroups.filter((_, i) => i !== idx)
            }
          : s
      )
    }));
  };

  const handleSubjectFacultyChange = (subjectId, facultyId) => {
    setForm(f => ({
      ...f,
      subjects: f.subjects.map(s =>
        s.subjectId === subjectId
          ? { ...s, facultyId }
          : s
      )
    }));
  };

  const handleViewGroup = async (group) => {
    for (const s of group.subjects) {
      try {
        const subjectId = typeof s.subjectId === 'object' && s.subjectId.$oid ? s.subjectId.$oid : s.subjectId;
        const response = await api.get(
          `/api/subjects/get-faculty-by-subject/${subjectId}/${tenantId || department?.tenantId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setFacultyBySubject(prev => ({
          ...prev,
          [subjectId]: mapFacultyList(response.data.faculty)
        }));
      } catch (err) {
        setFacultyBySubject(prev => ({ ...prev, [s.subjectId]: [] }));
      }
    }
    setViewGroup(group);
  };

  const toggleWhySubgroup = (subjectId) => {
    setShowWhySubgroup(prev => ({
      ...prev,
      [subjectId]: !prev[subjectId]
    }));
  };

  const getCurrentSubjects = () => {
    const base = form.groupCategory === 'language' ? formLanguageSubjects : formElectiveSubjects;
    return base;
  };

  const getGroupTypeDisplay = (groupCategory) => {
    return groupCategory === 'language' ? 'Language' : 'Elective';
  };

  const filteredGroups = groups.filter(group => {
    let match = true;
    if (selectedAcademicYear) match = match && group.academicYearId === selectedAcademicYear;
    
    const academicYear = academicYears.find(y => y._id === selectedAcademicYear);
    if (academicYear?.isSemesterBased) {
      if (selectedSemester) match = match && group.semesterId === selectedSemester;
    } else {
      if (selectedTerm) match = match && group.termId === selectedTerm;
    }
    return match;
  });

  const handleAcademicYearChange = (value) => {
    setForm(f => ({ ...f, academicYearId: value, semesterId: '', termId: '' }));
    const academicYear = academicYears.find(y => y._id === value);
    if (academicYear && !academicYear.isSemesterBased) {
      fetchTerms(value);
    }
  };

  return (
    <div className="space-y-5 p-2 sm:p-0">
      {message && (
        <MessageBanner ref={messageRef} type={messageType} onClose={() => setMessage('')}>
          {message}
        </MessageBanner>
      )}

      {/* Roster Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Subject Groups</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Manage elective and language groups for {department?.name || departmentId}</p>
        </div>
        <Button
          onClick={() => openModal()}
          variant={activeTab}
          className="w-full sm:w-auto text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Create {getGroupTypeDisplay(activeTab)} Group</span>
        </Button>
      </div>

      {/* Tabs */}
      <Card className="p-1 flex-shrink-0">
        <div className="flex border-b border-gray-100">
          <button
            type="button"
            className={`flex-1 py-3 px-2 text-center font-semibold text-xs sm:text-sm rounded-t-xl transition ${
              activeTab === 'elective'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('elective')}
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span>Elective Groups</span>
            </div>
          </button>
          <button
            type="button"
            className={`flex-1 py-3 px-2 text-center font-semibold text-xs sm:text-sm rounded-t-xl transition ${
              activeTab === 'language'
                ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('language')}
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span>Language Groups</span>
            </div>
          </button>
        </div>
      </Card>

      {/* Filters Configuration */}
      <Card className="p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Academic Year"
            value={selectedAcademicYear}
            onChange={e => setSelectedAcademicYear(e.target.value)}
          >
            <option value="">Select Academic Year</option>
            {academicYears.map(year => (
              <option key={year._id} value={year._id}>
                {year.name}{year.label ? ` (${year.label})` : ''}
              </option>
            ))}
          </Select>
          
          {isSemesterBased ? (
            <Select
              label="Semester"
              value={selectedSemester}
              onChange={e => setSelectedSemester(e.target.value)}
            >
              <option value="">Select Semester</option>
              {semesters.map(sem => (
                <option key={sem._id} value={sem._id}>{sem.semesterName}</option>
              ))}
            </Select>
          ) : (
            <Select
              label="Term"
              value={selectedTerm}
              onChange={e => setSelectedTerm(e.target.value)}
            >
              <option value="">Select Term</option>
              {terms.map(term => (
                <option key={term._id} value={term._id}>
                  {term.termLabel || term.termName || `Term ${term.termNumber}`}
                </option>
              ))}
            </Select>
          )}
        </div>
      </Card>

      {/* Groups Container Card */}
      <Card>
        <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">
            {getGroupTypeDisplay(activeTab)} Groups ({filteredGroups.length})
          </h2>
          <Badge variant={activeTab}>
            {getGroupTypeDisplay(activeTab)}
          </Badge>
        </div>
        
        {loading ? (
          <LoadingSpinner />
        ) : filteredGroups.length === 0 ? (
          <EmptyState
            title={`No ${getGroupTypeDisplay(activeTab).toLowerCase()} groups found`}
            description={`Get started by creating your first ${getGroupTypeDisplay(activeTab).toLowerCase()} group for the selected criteria.`}
            action={<Button onClick={() => openModal()} variant={activeTab} className="w-full sm:w-auto">Create {getGroupTypeDisplay(activeTab)} Group</Button>}
          />
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredGroups.map(group => (
              <div key={group._id} className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                      {group.groupName}
                    </h3>
                    {group.label && (
                      <span className="text-xs sm:text-sm text-gray-500 font-normal">({group.label})</span>
                    )}
                    <Badge variant="success">Active</Badge>
                    {group.termId && (
                      <Badge variant="info">Term-based</Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600">
                    <div className="break-words">
                      <span className="font-semibold text-gray-700">{group.groupCategory === 'language' ? 'Languages:' : 'Subjects:'}</span>{' '}
                      {group.subjects.map(s => {
                        const subjectsList = group.groupCategory === 'language' ? languageSubjects : electiveSubjects;
                        const subj = subjectsList.find(es => es.id === s.subjectId);
                        return subj ? subj.name : s.subjectId;
                      }).join(', ')}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">{group.groupCategory === 'language' ? 'Languages' : 'Subjects'} Count:</span> {group.subjects.length}
                    </div>
                    {group.termId && (
                      <div>
                        <span className="font-semibold text-gray-700">Term:</span>{' '}
                        {terms.find(t => t._id === group.termId)?.termLabel || 
                         terms.find(t => t._id === group.termId)?.termName || 
                         'Term'}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions Panel inside loops */}
                <div className="flex items-center justify-end gap-1.5 border-t border-gray-50 pt-3 lg:pt-0 lg:border-none flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewGroup(group)}
                    className="flex-1 sm:flex-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.272 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>View</span>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openModal(group)}
                    className="flex-1 sm:flex-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit</span>
                  </Button>
                  
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setGroupToDelete(group);
                      setShowDeleteModal(true);
                    }}
                    className="flex-1 sm:flex-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal Configuration */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`${editingGroup ? 'Edit' : 'Create'} ${getGroupTypeDisplay(form.groupCategory)} Group`}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden h-full">
          <div className="p-4 sm:p-6 space-y-5 overflow-y-auto max-h-[calc(100dvh-130px)] sm:max-h-[65vh] min-h-0">
            {/* Group Type Selection Block */}
            {!editingGroup && (
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">Group Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none select-none ${
                    form.groupCategory === 'elective'
                      ? 'border-blue-400 bg-blue-50/50 ring-2 ring-blue-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="groupCategory"
                      value="elective"
                      checked={form.groupCategory === 'elective'}
                      onChange={(e) => setForm(f => ({ ...f, groupCategory: e.target.value, subjects: [] }))}
                      className="sr-only"
                    />
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">Elective Group</div>
                        <div className="text-xs text-gray-500 mt-0.5">Regular elective subjects</div>
                      </div>
                      <svg className={`h-5 w-5 flex-shrink-0 ${form.groupCategory === 'elective' ? 'text-blue-600' : 'text-gray-300'}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </label>

                  <label className={`relative flex cursor-pointer rounded-xl border p-4 focus:outline-none select-none ${
                    form.groupCategory === 'language'
                      ? 'border-purple-400 bg-purple-50/50 ring-2 ring-purple-500'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="groupCategory"
                      value="language"
                      checked={form.groupCategory === 'language'}
                      onChange={(e) => setForm(f => ({ ...f, groupCategory: e.target.value, subjects: [] }))}
                      className="sr-only"
                    />
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">Language Group</div>
                        <div className="text-xs text-gray-500 mt-0.5">Language learning subjects</div>
                      </div>
                      <svg className={`h-5 w-5 flex-shrink-0 ${form.groupCategory === 'language' ? 'text-purple-600' : 'text-gray-300'}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Group Name"
                name="groupName"
                value={form.groupName}
                onChange={e => setForm(f => ({ ...f, groupName: e.target.value }))}
                error={formErrors.groupName}
                placeholder={`Enter ${form.groupCategory === 'language' ? 'language' : 'elective'} group name`}
                required
              />
              
              <Input
                label="Label (e.g. BTech, MCA, BBA)"
                name="label"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                error={formErrors.label}
                placeholder="Enter group label"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Academic Year"
                value={form.academicYearId}
                onChange={(e) => handleAcademicYearChange(e.target.value)}
                error={formErrors.academicYearId}
                required
              >
                <option value="">Select Academic Year</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>
                    {year.name}{year.label ? ` (${year.label})` : ''}
                  </option>
                ))}
              </Select>
              
              {form.academicYearId && (() => {
                const academicYear = academicYears.find(y => y._id === form.academicYearId);
                const isSemesterBasedYear = academicYear?.isSemesterBased || false;
                
                return isSemesterBasedYear ? (
                  <Select
                    label="Semester"
                    value={form.semesterId}
                    onChange={e => setForm(f => ({ ...f, semesterId: e.target.value }))}
                    error={formErrors.semesterId}
                    required
                  >
                    <option value="">Select Semester</option>
                    {semesters.map(sem => (
                      <option key={sem._id} value={sem._id}>{sem.semesterName}</option>
                    ))}
                  </Select>
                ) : (
                  <Select
                    label="Term"
                    value={form.termId}
                    onChange={e => setForm(f => ({ ...f, termId: e.target.value }))}
                    error={formErrors.termId}
                    required
                  >
                    <option value="">Select Term</option>
                    {terms.map(term => (
                      <option key={term._id} value={term._id}>
                        {term.termLabel || term.termName || `Term ${term.termNumber}`}
                      </option>
                    ))}
                  </Select>
                );
              })()}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2">
                Select {form.groupCategory === 'language' ? 'Languages' : 'Subjects'}
              </label>
              {formErrors.subjects && (
                <p className="text-red-600 text-xs font-semibold mb-3">{formErrors.subjects}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {getCurrentSubjects().map(subj => {
                  const checked = form.subjects.some(s => s.subjectId === subj.id);
                  return (
                    <label key={subj.id} className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors select-none">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => handleSubjectCheckbox(subj, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0"
                      />
                      <span className="flex-1 text-sm font-medium text-gray-700 truncate pr-2">{subj.name}</span>
                      {subj.code && (
                        <Badge variant="info" className="text-[10px] font-bold flex-shrink-0">{subj.code}</Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Subject/Language Inner Configuration Display */}
            {form.subjects.length > 0 && (
              <div className="space-y-4 pt-2">
                <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">
                  {form.groupCategory === 'language' ? 'Language' : 'Subject'} Configuration
                </h4>
                {form.subjects.map(subject => {
                  const subjectData = getCurrentSubjects().find(es => es.id === subject.subjectId);
                  return (
                    <Card key={subject.subjectId} className="p-4 bg-gray-50/40">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <h5 className="font-bold text-sm sm:text-base text-gray-800 break-words">{subjectData?.name}</h5>
                        <Badge variant="default" className="self-start sm:self-auto">{subject.subGroups?.length || 0} Subgroups</Badge>
                      </div>

                      {/* Subject Faculty Option */}
                      <div className="mb-4">
                        <Select
                          label="Primary Faculty"
                          value={subject.facultyId}
                          onChange={e => handleSubjectFacultyChange(subject.subjectId, e.target.value)}
                          error={formErrors[`subject-${subject.subjectId}`]}
                          disabled={subject.subGroups && subject.subGroups.length > 0}
                        >
                          <option value="">Select Faculty</option>
                          {(facultyBySubject[subject.subjectId] || []).map(fac => (
                            <option key={fac.id} value={fac.id}>
                              {fac.firstName} {fac.lastName} {fac.staffCode && `(${fac.staffCode})`}
                            </option>
                          ))}
                        </Select>
                        {subject.subGroups && subject.subGroups.length > 0 && (
                          <p className="text-xs text-amber-600 font-medium mt-1.5 flex items-center gap-1">
                            <span>⚠️</span> Faculty assignment is managed at subgroup level
                          </p>
                        )}
                      </div>

                      {/* Info Toggle Module */}
                      <div className="mb-3">
                        <button
                          type="button"
                          className="text-xs text-blue-600 font-semibold underline hover:text-blue-800 focus:outline-none"
                          onClick={() => toggleWhySubgroup(subject.subjectId)}
                        >
                          Why is a subgroup needed?
                          <span className="ml-1 text-[10px]">{showWhySubgroup[subject.subjectId] ? '▲' : '▼'}</span>
                        </button>
                        {showWhySubgroup[subject.subjectId] && (
                          <div className="mt-2 text-xs bg-blue-50 border border-blue-100 rounded-xl p-3.5 text-blue-800 leading-relaxed">
                            <b>Subgroups</b> are used when a {form.groupCategory === 'language' ? 'language' : 'subject'} needs to be split among multiple faculties due to a large number of students or for better management. 
                            <br /><br />
                            <b>Example:</b> If {form.groupCategory === 'language' ? 'Language' : 'Subject'} A has too many students, you can create two subgroups and assign a different faculty to each. 
                            <br /><br />
                            If no subgroup is needed, you can assign a faculty directly at the {form.groupCategory === 'language' ? 'language' : 'subject'} level.
                          </div>
                        )}
                      </div>

                      {/* Subgroups configuration */}
                      <div className="space-y-3 pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <label className="text-xs sm:text-sm font-semibold text-gray-700">Subgroups</label>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAddSubGroup(subject.subjectId)}
                          >
                            + Add Subgroup
                          </Button>
                        </div>
                        {(subject.subGroups || []).map((sg, idx) => (
                          <div key={idx} className="flex flex-col sm:flex-row sm:items-end gap-3 p-3.5 bg-white border border-gray-200 rounded-xl shadow-xs">
                            <div className="flex-1">
                              <Select
                                label={`Subgroup ${idx + 1} Faculty`}
                                value={sg.facultyId}
                                onChange={e => handleSubGroupChange(subject.subjectId, idx, 'facultyId', e.target.value)}
                                error={formErrors[`subgroup-${subject.subjectId}-${idx}`]}
                                className="w-full"
                              >
                                <option value="">Select Faculty</option>
                                {(facultyBySubject[subject.subjectId] || []).map(fac => (
                                  <option key={fac.id} value={fac.id}>
                                    {fac.firstName} {fac.lastName} {fac.staffCode && `(${fac.staffCode})`}
                                  </option>
                                ))}
                              </Select>
                            </div>
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveSubGroup(subject.subjectId, idx)}
                              className="w-full sm:w-auto"
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" onClick={handleSubmit} variant={form.groupCategory} className="w-full sm:w-auto">
              {editingGroup ? `Update Group` : `Create Group`}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View Group Details Modal */}
      <Modal
        isOpen={!!viewGroup}
        onClose={() => setViewGroup(null)}
        title="Group Details"
        size="lg"
      >
        {viewGroup && (
          <div className="flex flex-col h-full">
            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto max-h-[calc(100dvh-130px)] sm:max-h-[65vh]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Group Name</h4>
                  <p className="mt-1 text-base sm:text-lg font-bold text-gray-900 break-words">{viewGroup.groupName}</p>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Type</h4>
                  <div className="mt-1">
                    <Badge variant={viewGroup.groupCategory || 'elective'}>
                      {getGroupTypeDisplay(viewGroup.groupCategory)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Status</h4>
                  <div className="mt-1">
                    <Badge variant="success">Active</Badge>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Label</h4>
                  <p className="mt-1 text-sm font-semibold text-gray-800">{viewGroup.label || '-'}</p>
                </div>
                {viewGroup.termId && (
                  <div className="sm:col-span-2">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Term</h4>
                    <p className="mt-1 text-sm font-semibold text-gray-800">
                      {terms.find(t => t._id === viewGroup.termId)?.termLabel || 
                       terms.find(t => t._id === viewGroup.termId)?.termName || 
                       'Term'}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider text-gray-500">
                  Allocated {viewGroup.groupCategory === 'language' ? 'Languages' : 'Subjects'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {viewGroup.subjects.map((s, idx) => {
                    const subjectsList = viewGroup.groupCategory === 'language' ? languageSubjects : electiveSubjects;
                    const subj = subjectsList.find(es => es.id === s.subjectId);
                    const facultyList = facultyBySubject[s.subjectId] || [];
                    
                    return (
                      <Card key={idx} className="p-4 bg-white border border-gray-200">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h5 className="font-bold text-sm text-gray-900 line-clamp-2">{subj?.name || s.subjectId}</h5>
                          {s.subGroups && s.subGroups.length > 0 && (
                            <Badge variant="default" className="flex-shrink-0">{s.subGroups.length} Subgroups</Badge>
                          )}
                        </div>

                        {s.facultyId && (
                          <div className="text-xs sm:text-sm text-gray-600 mt-1">
                            <span className="font-semibold text-gray-500">Faculty:</span>{' '}
                            {facultyList.find(f => f.id === s.facultyId)?.firstName}{' '}
                            {facultyList.find(f => f.id === s.facultyId)?.lastName}
                          </div>
                        )}

                        {s.subGroups && s.subGroups.length > 0 && (
                          <div className="mt-3 pt-2.5 border-t border-gray-100 space-y-1.5">
                            <h6 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Subgroup Faculties:</h6>
                            <div className="space-y-1">
                              {s.subGroups.map((sg, sgIdx) => (
                                <div key={sgIdx} className="text-xs text-gray-700 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100 truncate font-medium">
                                  {sg.facultyId ? (
                                    <span>
                                      {facultyList.find(f => f.id === sg.facultyId)?.firstName}{' '}
                                      {facultyList.find(f => f.id === sg.facultyId)?.lastName}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 italic">Unassigned</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
            <ModalFooter>
              <Button onClick={() => setViewGroup(null)} className="w-full sm:w-auto">
                Close
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Alert Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Group"
        size="sm"
      >
        <div className="p-5 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-3 text-red-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5">Delete Group?</h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-2">
            Are you sure you want to delete <strong>{groupToDelete?.groupName}</strong>?
          </p>
          <p className="text-xs text-red-500 font-semibold bg-red-50 p-2 rounded-lg border border-red-100">This operations process cannot be reversed.</p>
        </div>
        
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            variant="danger"
            className="w-full sm:w-auto"
            onClick={async () => {
              setShowDeleteModal(false);
              await handleDeleteGroup(groupToDelete._id);
              setGroupToDelete(null);
            }}
          >
            Delete Group
          </Button>
        </ModalFooter>
      </Modal>

      {/* Success Receipt Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        size="sm"
      >
        <div className="p-5 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3 text-green-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Group Created!</h3>
          <p className="text-xs sm:text-sm text-gray-500">
            Your {form.groupCategory === 'language' ? 'language' : 'elective'} group parameters have been written successfully.
          </p>
        </div>
        
        <ModalFooter>
          <Button onClick={() => setShowSuccessModal(false)} className="w-full sm:w-auto">
            Continue
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ElectiveGroupManager;